import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCog, CreditCard, FileBarChart,
  ScrollText, MessageSquare, Cpu, LogOut, Settings, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import translations from "../pages/admin/adminTranslations";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLanguageContext = createContext();

export const useAdminLanguage = () => useContext(AdminLanguageContext);

const PERMISSION_MAP = {
  "/admin": { key: "view_dashboard", icon: LayoutDashboard },
  "/admin/users": { key: "manage_users", icon: Users },
  "/admin/employees": { key: "manage_employees", icon: UserCog },
  "/admin/billing": { key: "billing_access", icon: CreditCard },
  "/admin/interviews": { key: "view_interviews", icon: FileBarChart },
  "/admin/logs": { key: "logs_access", icon: ScrollText },
  "/admin/testimonials": { key: "view_testimonials", icon: MessageSquare },
  "/admin/ai-monitor": { key: "ai_monitor", icon: Cpu },
};

const ALL_NAV_ITEMS = [
  { icon: LayoutDashboard, labelKey: "dashboard", path: "/admin" },
  { icon: Users, labelKey: "users", path: "/admin/users" },
  { icon: UserCog, labelKey: "employees", path: "/admin/employees" },
  { icon: CreditCard, labelKey: "billing", path: "/admin/billing" },
  { icon: FileBarChart, labelKey: "interviews", path: "/admin/interviews" },
  { icon: ScrollText, labelKey: "logs", path: "/admin/logs" },
  { icon: MessageSquare, labelKey: "testimonials", path: "/admin/testimonials" },
  { icon: Cpu, labelKey: "ai_monitor", path: "/admin/ai-monitor" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState("fr");
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);

  const t = (key) => translations[language]?.[key] || translations.fr[key] || key;

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const userData = await res.json();
        if (userData.role !== "super_admin" && userData.role !== "admin_employee") {
          navigate("/admin/login");
          return;
        }
        setUser(userData);
      } else {
        navigate("/admin/login");
      }
    } catch {
      navigate("/admin/login");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    navigate("/admin/login");
  };

  const getFilteredNavItems = () => {
    if (!user) return [];
    if (user.role === "super_admin") return ALL_NAV_ITEMS;
    const perms = user.permissions || [];
    return ALL_NAV_ITEMS.filter(item => {
      const mapping = PERMISSION_MAP[item.path];
      return mapping && perms.includes(mapping.key);
    });
  };

  const navItems = getFilteredNavItems();

  const getInitials = (name) => {
    if (!name) return "A";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <AdminLanguageContext.Provider value={{ language, setLanguage, t }}>
      <div className="flex h-screen bg-zinc-950 text-zinc-100">
        {/* Sidebar */}
        <aside className={`${collapsed ? "w-16" : "w-64"} bg-zinc-900 border-r border-zinc-800 flex flex-col transition-all duration-200`}>
          {/* Logo */}
          <div className="p-4 flex items-center justify-between border-b border-zinc-800">
            {!collapsed && (
              <div className="flex items-center gap-2">
                <img src={require("../assets/ema-logo.png")} alt="Ema" className="h-8 w-auto" />
                <span className="text-xs text-zinc-500">Admin</span>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-zinc-400 hover:text-white h-8 w-8">
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>

          {/* User Profile Card */}
          {user && !collapsed && (
            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border-2 border-indigo-500">
                  <AvatarImage src={user.photo_url} />
                  <AvatarFallback className="bg-indigo-600 text-white text-sm">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
                  <p className="text-xs text-zinc-500 truncate">{user.position || (user.role === "super_admin" ? "Super Admin" : t("admin_employee"))}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    isActive
                      ? "bg-indigo-600/20 text-indigo-400 font-medium"
                      : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                  }`}
                  data-testid={`admin-nav-${item.labelKey}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span>{t(item.labelKey)}</span>}
                </button>
              );
            })}
          </nav>

          {/* Footer Actions */}
          <div className="p-2 border-t border-zinc-800 space-y-1">
            <button
              onClick={() => navigate("/admin/profile")}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors"
              data-testid="admin-nav-profile"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{t("edit") + " " + (user?.role === "super_admin" ? "Profil" : "Profil")}</span>}
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{t("logout")}</span>}
            </button>

            {/* Language Selector */}
            {!collapsed && (
              <div className="flex justify-center gap-1 pt-2">
                {["fr", "en", "es"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    className={`px-2 py-1 text-xs rounded uppercase font-medium transition-colors ${
                      language === lang ? "bg-indigo-600 text-white" : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet context={{ user, refreshUser: fetchUser }} />
        </main>
      </div>
    </AdminLanguageContext.Provider>
  );
}
